import { useCallback, useLayoutEffect, useRef } from 'react';
import { useI18n, type CopyKey } from '../i18n';
import { composerBox, composerKey, widthChanged } from './composer.js';
import type { Suggestion } from './suggest';

/**
 * The box, and it is never anywhere but the bottom of the screen.
 *
 * Nothing here is positioned. It is the last row of the chat's flex column and the column
 * is exactly as tall as the visible viewport, so the composer sits above the keyboard
 * because there is nowhere else for it to be — see `viewport.ts` for why every attempt to
 * pin it with `position: fixed` goes wrong on iOS.
 *
 * The textarea grows with what is typed, to a ceiling. A box with no ceiling eats the
 * conversation it is answering; a fixed one-row box makes a wrapped sentence scroll inside a
 * window it fits in. The ceiling and the arithmetic under it are in `composer.ts`, with the
 * decision about what Enter does, because both are worth a test and neither needs a browser.
 *
 * **What this file owns of that is the measuring**, which a stylesheet cannot do: how many
 * pixels a line is, how much padding and border surround it, and how tall the content would
 * be if nothing stopped it. F-148 changed the numbers those feed and not the shape of the
 * screen — nothing here is positioned, sized in viewport units, or aware of the keyboard.
 */

const WORDS: Record<Suggestion, CopyKey> = {
  dontKnow: 'chat.word.dontKnow',
  dispute: 'chat.word.dispute',
  more: 'chat.word.more',
  moreQuestions: 'chat.word.moreQuestions',
};

const CHIP_LABELS: Record<Suggestion, CopyKey> = {
  dontKnow: 'chat.chip.dontKnow',
  dispute: 'chat.chip.dispute',
  more: 'chat.chip.more',
  moreQuestions: 'chat.chip.moreQuestions',
};

export default function Composer({
  draft,
  onDraft,
  onSend,
  sending,
  suggestions,
}: {
  draft: string;
  onDraft: (next: string) => void;
  onSend: () => void;
  sending: boolean;
  suggestions: Suggestion[];
}) {
  const { t } = useI18n();
  const box = useRef<HTMLTextAreaElement | null>(null);

  /*
   * Grown to fit, by measuring rather than by counting newlines.
   *
   * A count of `\n` is wrong the moment a line wraps, which on a phone is most lines of a
   * real answer — the box would stay one row tall while three rows of text scrolled inside
   * it. Resetting to `auto` before reading `scrollHeight` is what makes it shrink again
   * when text is deleted; without that reset the height only ever ratchets up, and a
   * learner who pastes a paragraph and then clears it is left with a box that has eaten
   * half the thread.
   */
  const measure = useCallback(() => {
    const element = box.current;
    if (!element) return;
    element.style.height = 'auto';
    /*
     * Measured with scrolling off, whatever the last run left behind.
     *
     * A classic scrollbar — a desktop browser, and any platform where the learner has turned
     * overlay scrollbars off — takes about fifteen pixels out of the content box while it is
     * painted. `rows={1}` means the content genuinely overflows during the measurement, so a
     * box left `overflow-y: auto` by the previous run is measured fifteen pixels narrower than
     * it will be rendered: a line that fits wraps, and `scrollHeight` comes back up to a row
     * too tall. That is a character of hysteresis either side of the three-row boundary, where
     * one keystroke grows the box and deleting it again does not shrink it back. Nothing is
     * lost by it and it is invisible on iOS, but a measurement that depends on its own
     * previous answer is not a measurement.
     */
    element.style.overflowY = 'hidden';

    /*
     * Every term read from the element, none of them assumed.
     *
     * The padding and the border used to be one number — a `+ 24` standing in for both — and
     * both are set in `rem` in a stylesheet this file does not own, so the constant was
     * already a few pixels out and would have gone further out the first time either changed.
     * At a four-row ceiling that error was a sliver; at three rows it is a visible fraction of
     * a line. `line-height: normal` computes to a keyword rather than a length on some
     * browsers, which is what the fallback is for.
     */
    const style = getComputedStyle(element);
    const px = (value: string) => Number.parseFloat(value) || 0;
    const metrics = {
      lineHeight: px(style.lineHeight) || 24,
      paddingY: px(style.paddingTop) + px(style.paddingBottom),
      borderY: px(style.borderTopWidth) + px(style.borderBottomWidth),
      scrollHeight: element.scrollHeight,
      placeholderHeight: undefined as number | undefined,
    };

    /*
     * An empty box is sized for the placeholder, which `scrollHeight` does not account for.
     *
     * **A placeholder contributes nothing to `scrollHeight`**, so an empty textarea measures
     * one line however many the placeholder occupies — and "Answer the question, or ask for
     * something." takes two on a phone. The second line then overflowed a box padded for one,
     * which reads as a field whose bottom margin is smaller than its top. Reported exactly
     * that way.
     *
     * Measured by briefly putting the placeholder in as the value, rather than by mirroring it
     * into a hidden element. A mirror has to be kept in step with this box's width, font,
     * padding and wrapping rules for ever, and it is wrong the first time any of them changes;
     * the element measures itself here, so there is nothing to keep in step. Nothing is
     * painted in between — this is a layout effect and the value is restored in the same
     * synchronous block — and React's own `value` is untouched, so the DOM ends exactly as it
     * began.
     *
     * Shortening the copy was the other option and only hides it: the same sentence wraps
     * again in Spanish, at a larger Dynamic Type setting, or on a narrower handset. Sizing to
     * what is actually being shown is true at every width.
     */
    if (element.value === '' && element.placeholder !== '') {
      element.value = element.placeholder;
      metrics.placeholderHeight = element.scrollHeight;
      element.value = '';
    }

    const { height, scrolls } = composerBox(metrics);
    element.style.height = `${height}px`;
    /*
     * Scrolling is switched on only when there is somewhere to scroll to, and that is the
     * complaint rather than a refinement of it. Left `overflow-y: auto` at every height the
     * box was scrollable from the first character — the height was set from a `scrollHeight`
     * that excludes the border under a global `box-sizing: border-box`, so it was always two
     * pixels short of its own content — which is a scrollbar in a one-line field on a laptop
     * and a field that rubber-bands under a thumb on a phone. `composer.ts` fixes the height;
     * this stops the browser painting the affordance when the box is not scrollable at all.
     */
    element.style.overflowY = scrolls ? 'auto' : 'hidden';
    /*
     * **No dependencies, which is what the empty array below is.** Emptiness is asked of the
     * DOM rather than of the `draft` prop — the same answer, since React wrote that value —
     * so anything may call this, and the observer further down shares one measurement with the
     * effect instead of keeping a second copy of it in step.
     */
  }, []);

  /** Re-measured whenever what is typed changes, which is the ordinary case. */
  useLayoutEffect(() => {
    measure();
  }, [draft, measure]);

  /*
   * And re-measured when the box changes width, which is the case that has no keystroke.
   *
   * **Without this the height is pinned to a wrapping that no longer exists.** Two lines typed
   * in landscape rewrap to four in portrait; the height stays at two rows and `overflow-y` is
   * still `hidden`, because the only thing that recomputes either is a keystroke — so two
   * lines of the learner's own answer are on screen nowhere and cannot be scrolled to, until
   * the next character heals it. The text is all still there and the send button still sends
   * all of it, which is exactly the shape of failure this project keeps meeting: nothing
   * errors, nothing is lost, and what is on screen is wrong.
   *
   * Rotation is the obvious way in and not the only one — a desktop window dragged narrower,
   * a chip row appearing beside it, a scrollbar arriving on the thread. All of them are a
   * width change on this element, so the element is what is observed.
   *
   * `ResizeObserver` reached Safari in iOS 13.4 and is older than that everywhere else, so the
   * feature test is not expected to fire. It costs a line, and what it buys is a box that
   * measures on keystrokes alone rather than a screen that throws on mount.
   */
  useLayoutEffect(() => {
    const element = box.current;
    if (!element) return;
    if (typeof ResizeObserver === 'undefined') return;

    let width: number | undefined;
    const observer = new ResizeObserver((entries) => {
      const next = entries[entries.length - 1]?.contentRect.width;
      if (next === undefined) return;
      const again = widthChanged(width, next);
      // Banked before the measurement and not after it: a measurement can change the width
      // (a scrollbar appearing is about fifteen pixels of it), and remembering the old one
      // across that write is a third callback that finds a change nobody made. `widthChanged`
      // in `composer.ts` sets out the whole termination argument, of which this line is a
      // third — it is not a tidier way of writing the same thing.
      width = next;
      if (again) measure();
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [measure]);

  /*
   * One predicate for the key and the button, so they cannot disagree about whether there is
   * anything to send. `Chat.tsx` guards `send()` on the same two conditions; this is what the
   * learner sees of that guard rather than a second copy of it.
   */
  const canSend = !sending && draft.trim() !== '';

  const fill = (suggestion: Suggestion) => {
    onDraft(t(WORDS[suggestion]));
    box.current?.focus();
  };

  return (
    <form
      className="chat-composer"
      onSubmit={(event) => {
        event.preventDefault();
        onSend();
      }}
    >
      {suggestions.length > 0 && (
        <div className="chat-chips" role="group" aria-label={t('chat.chips.label')}>
          {suggestions.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              className="chat-chip"
              onClick={() => fill(suggestion)}
            >
              {t(CHIP_LABELS[suggestion])}
            </button>
          ))}
        </div>
      )}

      <div className="chat-composer-row">
        <label className="chat-sr" htmlFor="chat-draft">
          {t('chat.input.label')}
        </label>
        <textarea
          id="chat-draft"
          ref={box}
          className="chat-draft"
          value={draft}
          onChange={(event) => onDraft(event.target.value)}
          placeholder={t('chat.input.placeholder')}
          rows={1}
          /* Sentences, because an answer is one. iOS otherwise applies the last setting the
             page used, and a capital letter missing from the front of every answer reads as
             the app being careless rather than as a browser default. */
          autoCapitalize="sentences"
          /* Enter sends, Shift+Enter is the newline — F-148, and `composer.ts` argues it.
             `enterkeyhint` puts it on the key itself, which is the only place a phone keyboard
             can say so; the hint below says it for anything that reads the field aloud. */
          enterKeyHint="send"
          aria-describedby="chat-draft-hint"
          onKeyDown={(event) => {
            /*
             * The native event, not React's, and not for tidiness. React's synthetic keyboard
             * event does not carry `isComposing` at all — it is absent from the type and from
             * the object — so the composition guard that stops this eating a half-typed
             * Japanese or Korean character can only be asked of the event the browser made.
             */
            const action = composerKey(
              {
                key: event.key,
                shiftKey: event.shiftKey,
                altKey: event.altKey,
                metaKey: event.metaKey,
                ctrlKey: event.ctrlKey,
                isComposing: event.nativeEvent.isComposing,
                keyCode: event.nativeEvent.keyCode,
              },
              canSend,
            );
            if (action === 'pass') return;
            event.preventDefault();
            /*
             * No `blur()` after sending, deliberately. Dismissing the keyboard would take the
             * focus that `chatCeilingFor` treats as the precondition for a keyboard being up,
             * so the chat would unclamp and re-clamp on every answer — the screen jumping once
             * per message — and the learner would have to tap the box again to answer the next
             * question. The box keeps focus and empties.
             */
            if (action === 'send') onSend();
          }}
        />
        <button
          type="submit"
          className="chat-send"
          disabled={!canSend}
          aria-label={t('chat.send')}
        >
          {/* An arrow rather than the word, because the word is three times the width on a
              row that also has to hold a box worth typing in, and every chat a learner has
              ever used puts an arrow here. The word is still on the button for anything
              that reads it aloud. */}
          <span aria-hidden="true">{sending ? '…' : '↑'}</span>
        </button>
      </div>

      {/* Costs no height and never wraps the row: `chat-sr` takes it out of the flow
          entirely, which matters because this composer's height is the thread's loss. */}
      <span id="chat-draft-hint" className="chat-sr">
        {t('chat.input.hint')}
      </span>
    </form>
  );
}
